import React from "react";
import {
  Box,
  Typography,
  Container,
  Divider,
  Stack,
  Paper,
  Grid,
} from "@mui/material";
import { useSelector } from "react-redux";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FadeInOnScroll from "../common/FadeInOnScroll";
import { CONTACT_PHONES, toTelHref } from "../../constants/contactPhones";

const WHY_CHOOSE = [
  {
    title: "Customized Solutions",
    text: "Tailored property solutions based on each client's requirements.",
  },
  {
    title: "Expert Guidance",
    text: "Professional support throughout the real estate journey.",
  },
  {
    title: "Market Insight",
    text: "Relevant knowledge of current property market trends and opportunities.",
  },
  {
    title: "Comprehensive Services",
    text: "Support from property evaluation and marketing through negotiation and closing.",
  },
  {
    title: "Client Satisfaction",
    text: "A strong focus on understanding client needs and delivering dependable service.",
  },
];

const AboutUs = () => {
  const brand = useSelector((s) => s.themeValue?.colors) || {};
  const contact = useSelector((s) => s.themeValue?.contact || {});
  const start = brand.primary_gradient_start_color || "#FF2D95";
  const end = brand.primary_gradient_end_color || "#7B1FA2";

  return (
    <Box sx={{ bgcolor: "#FBF8FC", py: { xs: 5, md: 7 } }}>
      <Container maxWidth="md">
        <FadeInOnScroll variant="fade-in">
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1.5} sx={{ mb: 1.5 }}>
          <Box sx={{ width: 36, height: 2, bgcolor: end, borderRadius: 1 }} />
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: end,
            }}
          >
            About Us
          </Typography>
          <Box sx={{ width: 36, height: 2, bgcolor: end, borderRadius: 1 }} />
        </Stack>

        <Typography
          variant="h4"
          align="center"
          fontWeight={800}
          sx={{ mb: 3, color: "#1A1A2E", fontSize: { xs: "1.6rem", md: "2rem" } }}
        >
          E&amp;J Global Realtors
        </Typography>

        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.75, color: "text.secondary" }}>
          E&amp;J Global Realtors is a trusted real estate consulting company in
          Ponmar, Chennai, providing professional property consulting and
          comprehensive real estate services. With a strong understanding of the
          real estate market, we help clients make informed property decisions
          and achieve their real estate goals.
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.75, color: "text.secondary" }}>
          We offer personalized solutions for buying, selling, property
          management, evaluation, marketing, negotiation, and closing, based on
          each client&apos;s unique requirements. Our approach focuses on
          delivering reliable service, market insights, and long-term value to
          every client.
        </Typography>
        </FadeInOnScroll>

        <FadeInOnScroll>
        <Divider sx={{ my: 4, borderColor: `${end}33` }} />

        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ mb: 1.5, color: end, fontSize: "1.35rem" }}
        >
          Our Mission
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.75, color: "text.secondary" }}>
          Our mission is to provide exceptional real estate consulting services
          that help clients navigate the property market with confidence. We are
          committed to building long-term relationships through trust,
          integrity, professional guidance, and client-focused service.
        </Typography>
        </FadeInOnScroll>

        <FadeInOnScroll>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ mb: 1, color: end, fontSize: "1.35rem" }}
        >
          Why Choose E&amp;J Global Realtors?
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          What sets us apart in every property journey.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          {WHY_CHOOSE.map((item) => (
            <Grid item xs={12} sm={6} key={item.title}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.25,
                  height: "100%",
                  borderRadius: "5px",
                  border: "1px solid #ebe6ee",
                  bgcolor: "#fff",
                  borderLeft: `4px solid ${start}`,
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <CheckCircleOutlineIcon sx={{ color: start, mt: 0.2, fontSize: 22 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#1A1A2E", mb: 0.5 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
                      {item.text}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
        </FadeInOnScroll>

        <FadeInOnScroll>
        <Divider sx={{ my: 3, borderColor: `${end}33` }} />

        <Typography variant="h6" fontWeight={700} sx={{ mb: 1, color: "#1A1A2E" }}>
          Contact Us
        </Typography>
        <Typography variant="body1" sx={{ lineHeight: 1.8, color: "text.secondary" }}>
          Have questions? We&apos;re here to help!
          <br />
          {contact.email && (
            <>
              Email:{" "}
              <Box
                component="a"
                href={`mailto:${contact.email}`}
                sx={{ color: start, fontWeight: 600, textDecoration: "none" }}
              >
                {contact.email}
              </Box>
              <br />
            </>
          )}
          {CONTACT_PHONES.length > 0 && (
            <>
              Phone:{" "}
              {CONTACT_PHONES.map((phone, i) => (
                <React.Fragment key={phone}>
                  {i > 0 && " / "}
                  <Box
                    component="a"
                    href={toTelHref(phone)}
                    sx={{ color: start, fontWeight: 600, textDecoration: "none" }}
                  >
                    {phone}
                  </Box>
                </React.Fragment>
              ))}
              <br />
            </>
          )}
          {contact.address && <>Address: {contact.address}</>}
          {!contact.email && (
            <>
              Email:{" "}
              <a href="mailto:jemins_realmart@jemins.in">jemins_realmart@jemins.in</a>
              <br />
            </>
          )}
        </Typography>
        </FadeInOnScroll>
      </Container>
    </Box>
  );
};

export default AboutUs;
